import { useEffect, useState } from 'react';

/**
 * Custom hook to animate a number counting up to a target value.
 * @param target - The target number to count up to.
 * @param duration - The duration of the animation in milliseconds (default: 1000).
 * @param format - Whether to format the number with commas for thousands (default: false).
 * @param delay - The delay before starting the animation in milliseconds (default: 0).
 * @returns The current animated count value, formatted if specified.
 */
interface UseCountUpProps {
  target: number;
  duration?: number;
  format?: boolean;
  prefix?: string;
  suffix?: string;
  delay?: number;
}

const useCountUp = ({
  target,
  duration = 1000,
  format = false,
  prefix = '',
  suffix = '',
  delay = 0
}: UseCountUpProps) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    let startTime: number | null = null;
    let timeoutId: NodeJS.Timeout | null = null;

    if (target === 0) {
      setCount(0);
      return;
    }

    function animateCount(timestamp: number) {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * (target - start) + start));
      if (progress < 1) {
        requestAnimationFrame(animateCount);
      } else {
        setCount(target);
      }
    }

    const startAnimation = () => {
      requestAnimationFrame(animateCount);
    };

    if (delay > 0) {
      timeoutId = setTimeout(startAnimation, delay);
    } else {
      startAnimation();
    }

    // Reset on target change and cleanup timeout
    return () => {
      setCount(0);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [target, duration, delay]);
  const formatted = format ? count.toLocaleString() : count;
  return `${prefix}${formatted}${suffix}`;
};

export default useCountUp;
